import Dialog from "../../General/ConfirmableDialog";
import {
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ModeCommentOutlinedIcon from "@mui/icons-material/ModeCommentOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LocalGasStationOutlinedIcon from "@mui/icons-material/LocalGasStationOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import dayjs from "../../../utils/dayjsTimezone";
import { AutoSizer, MultiGrid, MultiGridProps } from "react-virtualized";
import { SelectChangeEvent } from "@mui/material/Select";
import { useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { operationsGrossTargets } from "../../../data/operationsGrossTargets";
import TruckRouteFuelDialog from "./TruckRouteFuelDialog";
import CheckBrokerButton from "../BrokerBlacklist/CheckBrokerButton";
import BrokerBlacklistWarning from "../BrokerBlacklist/BrokerBlacklistWarning";

type PlannerEntityType =
  | "Driver"
  | "Truck"
  | "Trailer"
  | "OperationsPlannerEntry";

interface PlannerEntityCommentDto {
  entityId: string;
  entityType: PlannerEntityType;
  comment: string | null;
}

interface AssignmentResponse {
  dispatcherId?: string | null;
  dispatcherFirstName?: string | null;
  dispatcherLastName?: string | null;
  dispatcherNickname?: string | null;
  dispatcherWorkPhoneNumber?: string | null;
  driverHasPlannerComment?: boolean;
  truckHasPlannerComment?: boolean;
  trailerHasPlannerComment?: boolean;
  driver?: {
    id?: string;
    firstName?: string | null;
    lastName?: string | null;
    phoneNumber?: string | null;
    driverType?: string | null;
    type?: string | null;
    company?: string | null;
  } | null;
  truck?: {
    unitNumber?: string | null;
    truckNumber?: string | null;
    id?: string | null;
  } | null;
  trailer?: {
    unitNumber?: string | null;
    trailerType?: string | null;
    type?: string | null;
    id?: string | null;
  } | null;
}

interface OperationsPlannerEntry {
  brokerName?: string | null;
  brokerEmail?: string | null;
  brokerMcNumber?: string | null;
  brokerDotNumber?: string | null;
  id: string;
  date: string;
  dispatcherId?: string | null;
  dispatcherFirstName?: string | null;
  dispatcherLastName?: string | null;
  dispatcherNickname?: string | null;
  driverId?: string | null;
  driverName?: string | null;
  truckId?: string | null;
  truckUnitNumber?: string | null;
  trailerId?: string | null;
  trailerUnitNumber?: string | null;
  gross?: number | null;
  mileage?: number | null;
  noLoadBooked?: boolean;
  rcReceivedButNotLoaded?: boolean;
  loadPickedUp?: boolean;
  inTransit?: boolean;
  delivered?: boolean;
  emptyOrStuck?: boolean;
  emptyOrStuckNote?: string | null;
  emptyOrStuckNoteTimestampUtc?: string | null;
  emptyOrStuckNoteTimestampChicago?: string | null;
  shopOrYard?: boolean;
  hometimeOrOff?: boolean;
  pickupLocation?: string | null;
  isMonthlyTotal?: boolean;
  monthlyGross?: number | null;
  monthlyRpm?: number | null;
  monthlyComment?: string | null;
  existingPlannerEntryId?: string | null;
  differentDriver?: boolean;
  differentTruck?: boolean;
  differentTrailer?: boolean;
  driverCompany?: string | null;
  trailerType?: string | null;
  isPreTripMade?: boolean | null;
}

interface OperationsPlannerEntryPayload {
  brokerName?: string;
  brokerEmail?: string;
  brokerMcNumber?: string;
  brokerDotNumber?: string;
  id?: string;
  date: string;
  dispatcherId?: string;
  dispatcherFirstName?: string;
  dispatcherLastName?: string;
  dispatcherNickname?: string;
  driverId?: string;
  driverName?: string;
  truckId?: string | null;
  truckUnitNumber?: string;
  trailerId?: string | null;
  trailerUnitNumber?: string;
  gross?: number | null;
  mileage?: number | null;
  noLoadBooked: boolean;
  rcReceivedButNotLoaded: boolean;
  loadPickedUp: boolean;
  inTransit: boolean;
  delivered: boolean;
  emptyOrStuck: boolean;
  emptyOrStuckNote?: string;
  shopOrYard: boolean;
  hometimeOrOff: boolean;
  pickupLocation?: string;
}

interface PlannerRow {
  dispatcherId: string;
  dispatcherName: string;
  dispatcherWorkPhoneNumber?: string | null;
  driverId: string;
  driverName: string;
  companyName: string;
  phoneNumber: string;
  driverType: string;
  truckUnitNumber: string;
  trailerUnitNumber: string;
  trailerType: string;
  trailerCategory: TrailerCategory;
  truckId?: string;
  trailerId?: string;
  driverHasPlannerComment: boolean;
  truckHasPlannerComment: boolean;
  trailerHasPlannerComment: boolean;
  isFirstDriver: boolean;
}

interface CommentDialogState {
  entityId: string;
  entityType: PlannerEntityType;
  label: string;
  initialComment?: string | null;
  hasExistingComment: boolean;
}

interface EditorState {
  row: PlannerRow;
  day: number;
  date: Dayjs;
  entry?: OperationsPlannerEntry;
  isDifferentPairEntry?: boolean;
}

interface PlannerEntryForm {
  brokerName: string;
  brokerEmail: string;
  brokerMcNumber: string;
  brokerDotNumber: string;
  id?: string;
  date: string;
  dispatcherId?: string;
  driverId: string;
  driverName: string;
  truckId?: string;
  trailerId?: string;
  truckUnitNumber: string;
  trailerUnitNumber: string;
  gross: string;
  mileage: string;
  noLoadBooked: boolean;
  rcReceivedButNotLoaded: boolean;
  loadPickedUp: boolean;
  inTransit: boolean;
  delivered: boolean;
  emptyOrStuck: boolean;
  emptyOrStuckNote: string;
  shopOrYard: boolean;
  hometimeOrOff: boolean;
  pickupLocation: string;
  selectedDates: string[];
}

type PlannerViewMode = "default" | "driver" | "truck" | "trailer";

const STATUS_OPTIONS: {
  field: keyof PlannerEntryForm;
  key: PlannerStatusKey;
  label: string;
}[] = [
  {
    field: "noLoadBooked",
    key: "noLoad",
    label: "No load booked yet for the day",
  },
  {
    field: "rcReceivedButNotLoaded",
    key: "rcReceived",
    label: "RC received but not loaded yet",
  },
  {
    field: "loadPickedUp",
    key: "loadPickedUp",
    label: "Load picked up",
  },
  {
    field: "inTransit",
    key: "inTransit",
    label: "In transit",
  },
  {
    field: "delivered",
    key: "delivered",
    label: "Delivered",
  },
  {
    field: "emptyOrStuck",
    key: "emptyOrStuck",
    label: "Empty or stuck until next day",
  },
  {
    field: "shopOrYard",
    key: "shopOrYard",
    label: "Shop / Yard",
  },
  {
    field: "hometimeOrOff",
    key: "hometimeOff",
    label: "Hometime / Off",
  },
];

const TRAILER_FILTER_OPTIONS: {
  value: TrailerCategoryFilter;
  label: string;
}[] = [
  { value: "all", label: "All trailer types" },
  { value: "rgn", label: "RGN only" },
  { value: "flatbed", label: "Flatbed / Stepdeck / Conestoga" },
  { value: "dryvan", label: "Dryvan / Reefer" },
];

const STATUS_LABEL_MAP: Record<PlannerStatusKey, string> =
  STATUS_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.key]: option.label }),
    {} as Record<PlannerStatusKey, string>,
  );

type PlannerStatusKey =
  | "noLoad"
  | "rcReceived"
  | "loadPickedUp"
  | "inTransit"
  | "delivered"
  | "emptyOrStuck"
  | "shopOrYard"
  | "hometimeOff";

type TrailerCategory = "rgn" | "flatbed" | "dryvan" | "other";
type TrailerCategoryFilter = TrailerCategory | "all";
type PlannerAssetFilterType = "truck" | "trailer" | null;

const buildDispatcherName = (
  nickname?: string | null,
  firstName?: string | null,
  lastName?: string | null,
): string =>
  nickname?.trim() ||
  `${firstName ?? ""} ${lastName ?? ""}`.trim() ||
  "Unassigned";

const formatPlannerDate = (date: Dayjs | string): string =>
  dayjs(date).format("YYYY-MM-DD");

const getPlannerDateKey = (date: string): string => {
  if (!date) return "";
  if (date.length >= 10) {
    return date.slice(0, 10);
  }

  const normalized = dayjs(date);
  if (!normalized.isValid()) {
    return date;
  }

  return normalized.format("YYYY-MM-DD");
};

const serializePlannerDate = (date: string): string => {
  const normalized = dayjs(date);
  if (!normalized.isValid()) {
    return date;
  }

  return `${normalized.format("YYYY-MM-DD")}T00:00:00`;
};

const createEmptyFormState = (): PlannerEntryForm => ({
  brokerName: "",
  brokerEmail: "",
  brokerMcNumber: "",
  brokerDotNumber: "",
  date: "",
  dispatcherId: undefined,
  driverId: "",
  driverName: "",
  truckId: undefined,
  trailerId: undefined,
  truckUnitNumber: "",
  trailerUnitNumber: "",
  gross: "",
  mileage: "",
  noLoadBooked: false,
  rcReceivedButNotLoaded: false,
  loadPickedUp: false,
  inTransit: false,
  delivered: false,
  emptyOrStuck: false,
  emptyOrStuckNote: "",
  shopOrYard: false,
  hometimeOrOff: false,
  pickupLocation: "",
  selectedDates: [],
});

const STATUS_FIELDS: (keyof PlannerEntryForm)[] = [
  "noLoadBooked",
  "rcReceivedButNotLoaded",
  "loadPickedUp",
  "inTransit",
  "delivered",
  "emptyOrStuck",
  "shopOrYard",
  "hometimeOrOff",
];

const getCompanyColor = (company?: string) => {
  switch (company?.toLowerCase()) {
    case "fox c group":
      return "#fdecec";
    case "c1 transportation":
      return "#fff2e0";
    default:
      return undefined;
  }
};

const getStatusKeyFromEntry = (
  entry?: OperationsPlannerEntry,
): PlannerStatusKey | null => {
  if (entry?.hometimeOrOff) return "hometimeOff";
  if (entry?.shopOrYard) return "shopOrYard";
  if (entry?.emptyOrStuck) return "emptyOrStuck";
  if (entry?.delivered) return "delivered";
  if (entry?.inTransit) return "inTransit";
  if (entry?.loadPickedUp) return "loadPickedUp";
  if (entry?.rcReceivedButNotLoaded) return "rcReceived";
  if (entry?.noLoadBooked) return "noLoad";
  return null;
};

const getTrailerCategory = (trailerType?: string | null): TrailerCategory => {
  const normalizedType = (trailerType ?? "").toLowerCase();

  if (normalizedType.includes("rgn")) {
    return "rgn";
  }

  if (
    normalizedType.includes("flat") ||
    normalizedType.includes("step") ||
    normalizedType.includes("conestoga")
  ) {
    return "flatbed";
  }

  if (
    normalizedType.includes("dry") ||
    normalizedType.includes("dryvan") ||
    normalizedType.includes("van") ||
    normalizedType.includes("reefer")
  ) {
    return "dryvan";
  }

  return "other";
};

const getTargetCategoryFromTrailerType = (
  trailerType?: string | null,
): keyof (typeof operationsGrossTargets)[string] | null => {
  const normalized = (trailerType ?? "").toLowerCase();

  if (!normalized.trim()) return "po";

  if (normalized.includes("rgn")) return "rgn";
  if (normalized.includes("step")) return "step";
  if (normalized.includes("conestoga")) return "conestoga";
  if (normalized.includes("reefer")) return "reefer";
  if (normalized.includes("power only") || normalized.includes("po"))
    return "po";
  if (normalized.includes("flat")) return "flat";
  if (normalized.includes("dry") || normalized.includes("van")) return "dryvan";

  return null;
};

const findTargetsForMonth = (
  month: Dayjs,
): Record<string, { gross: number; rpm: number }> => {
  const availableMonths = Object.keys(operationsGrossTargets)
    .map((key) => dayjs(key))
    .filter((value) => value.isValid())
    .sort((a, b) => b.valueOf() - a.valueOf());

  const matchingMonth = availableMonths.find(
    (value) => value.isSame(month, "month") || value.isBefore(month),
  );

  const fallbackKey =
    matchingMonth?.format("YYYY-MM") || availableMonths[0]?.format("YYYY-MM");

  if (fallbackKey && operationsGrossTargets[fallbackKey]) {
    return operationsGrossTargets[fallbackKey];
  }

  return {};
};

const FIXED_COLUMNS = [
  { key: "dispatcher" as const, label: "Dispatcher", width: 200 },
  { key: "driver" as const, label: "Driver", width: 220 },
  { key: "company" as const, label: "Company", width: 220 },
  { key: "phone" as const, label: "Phone", width: 150 },
  { key: "driverType" as const, label: "Driver Type", width: 140 },
  { key: "truck" as const, label: "Truck #", width: 100 },
  { key: "trailer" as const, label: "Trailer #", width: 110 },
  { key: "trailerType" as const, label: "Trailer Type", width: 160 },
];

const MANDATORY_COLUMN_KEYS = new Set([
  "driver",
  "truck",
  "trailer",
  "trailerType",
]);

const VIEW_MODE_COLUMN_KEYS: Record<PlannerViewMode, Set<string>> = {
  default: new Set(["driver", "truck", "trailer", "trailerType"]),
  driver: new Set(["driver", "company", "phone", "driverType"]),
  truck: new Set(["truck"]),
  trailer: new Set(["trailer", "trailerType"]),
};

const DAY_COLUMN_WIDTH = 140;
const SUMMARY_COLUMNS = [
  { key: "monthlyTargetGross" as const, label: "Target Gross", width: 140 },
  { key: "monthlyGross" as const, label: "Gross", width: 140 },
  { key: "monthlyTargetRpm" as const, label: "Target RPM", width: 120 },
  { key: "monthlyRpm" as const, label: "RPM", width: 120 },
  { key: "monthlyComment" as const, label: "Comment", width: 200 },
];
const HEADER_HEIGHT = 56;
const ROW_HEIGHT = 96;

const fetchPlannerAssignments = async (
  token: string | undefined | null,
): Promise<AssignmentResponse[]> => {
  if (!token) return [];

  const response = await fetch(`${apiVariable}/getDispatcherAssignments`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Unable to load planner data");
  }

  return response.json();
};

const fetchPlannerEntries = async (
  token: string | undefined | null,
  startDateIso: string,
  endDateIso: string,
  dispatcherId?: string,
): Promise<OperationsPlannerEntry[]> => {
  if (!token) return [];

  const params = new URLSearchParams({
    startDate: startDateIso,
    endDate: endDateIso,
  });

  if (dispatcherId) {
    params.append("dispatcherId", dispatcherId);
  }

  const response = await fetch(
    `${apiVariable}/getOperationsPlannerEntriesByDateRange?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Unable to load planner entries");
  }

  return response.json();
};

const fetchPlannerEntityComment = async (
  token: string | undefined | null,
  entityId: string,
  entityType: PlannerEntityType,
): Promise<PlannerEntityCommentDto | null> => {
  if (!token) return null;

  const params = new URLSearchParams({
    entityType,
    entityId,
  });

  const response = await fetch(
    `${apiVariable}/getPlannerEntityComment?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Unable to load planner comment");
  }

  return response.json();
};

const setPlannerEntityComment = async (
  token: string,
  payload: PlannerEntityCommentDto,
): Promise<PlannerEntityCommentDto> => {
  const response = await fetch(`${apiVariable}/setPlannerEntityComment`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Unable to save planner comment");
  }

  return response.json();
};

interface PlannerCommentControlsProps {
  token?: string | null;
  entityId?: string;
  entityType: PlannerEntityType;
  hasComment: boolean;
  label: string;
  onEdit: (details: {
    entityId: string;
    entityType: PlannerEntityType;
    label: string;
    initialComment?: string | null;
    hasExistingComment: boolean;
  }) => void;
}

const PlannerCommentControls = ({
  token,
  entityId,
  entityType,
  hasComment,
  label,
  onEdit,
}: PlannerCommentControlsProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const commentQuery = useQuery({
    queryKey: ["plannerEntityComment", entityType, entityId ?? "unknown"],
    queryFn: () =>
      fetchPlannerEntityComment(token, entityId as string, entityType),
    enabled: false,
    staleTime: 5 * 60 * 1000,
  });

  if (!entityId) {
    return null;
  }

  const handleTooltipOpen = async () => {
    if (!hasComment) return;
    setTooltipOpen(true);
    if (!commentQuery.data && !commentQuery.isFetching) {
      await commentQuery.refetch();
    }
  };

  const handleTooltipClose = () => setTooltipOpen(false);

  const handleEditClick = async () => {
    let initialComment: string | null | undefined = hasComment
      ? undefined
      : null;
    if (hasComment && !commentQuery.data && !commentQuery.isFetching) {
      const result = await commentQuery.refetch();
      initialComment = result.data?.comment ?? null;
    }

    onEdit({
      entityId,
      entityType,
      label,
      initialComment,
      hasExistingComment: hasComment,
    });
  };

  const tooltipTitle = commentQuery.error
    ? (commentQuery.error as Error).message
    : commentQuery.isFetching
      ? "Loading comment..."
      : commentQuery.data?.comment || "View comment";

  return (
    <>
      {hasComment && (
        <Tooltip
          title={tooltipTitle}
          arrow
          placement="top-end"
          open={tooltipOpen}
          onOpen={handleTooltipOpen}
          onClose={handleTooltipClose}
        >
          <IconButton
            size="small"
            color="primary"
            sx={{ position: "absolute", top: 4, right: 4 }}
          >
            <ModeCommentOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip
        title={hasComment ? "Edit comment" : "Add comment"}
        arrow
        placement="top"
      >
        <IconButton
          size="small"
          onClick={handleEditClick}
          sx={{ position: "absolute", bottom: 4, right: 4 }}
        >
          <NoteAltOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
};

const createPlannerEntry = async (
  token: string,
  payload: OperationsPlannerEntryPayload,
): Promise<OperationsPlannerEntry> => {
  const response = await fetch(`${apiVariable}/createOperationsPlannerEntry`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Unable to create planner entry");
  }

  return response.json();
};

const updatePlannerEntry = async (
  token: string,
  payload: OperationsPlannerEntryPayload,
): Promise<OperationsPlannerEntry> => {
  if (!payload.id) {
    throw new Error("Missing entry id for update");
  }

  const response = await fetch(
    `${apiVariable}/updateOperationsPlannerEntry/${payload.id}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    throw new Error("Unable to update planner entry");
  }

  return response.json();
};

const OperationsPlanner = () => {
  const theme = useTheme();
  const compactPlanner = useMediaQuery(theme.breakpoints.down(1280));
  const { token, role } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();

  const plannerAssetFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const assetType = params.get("assetType");
    const assetId = params.get("assetId");
    const unitNumber = params.get("unitNumber");

    const normalizedAssetType: PlannerAssetFilterType =
      assetType === "truck" || assetType === "trailer" ? assetType : null;

    return {
      assetType: normalizedAssetType,
      assetId: assetId?.trim() || "",
      unitNumber: unitNumber?.trim() || "",
    };
  }, [location.search]);
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf("month"));
  const [selectedDispatcherId, setSelectedDispatcherId] =
    useState<string>("all");
  const [plannerViewMode, setPlannerViewMode] =
    useState<PlannerViewMode>("default");
  const [selectedTrailerCategory, setSelectedTrailerCategory] =
    useState<TrailerCategoryFilter>("all");
  const [truckUnitSearch, setTruckUnitSearch] = useState("");
  const [showDetailsColumns, setShowDetailsColumns] = useState(false);
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [formValues, setFormValues] = useState<PlannerEntryForm>(() =>
    createEmptyFormState(),
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [brokerCheckPending, setBrokerCheckPending] = useState(false);
  const [commentDialogState, setCommentDialogState] =
    useState<CommentDialogState | null>(null);
  const [commentText, setCommentText] = useState("");
  const [commentError, setCommentError] = useState<string | null>(null);
  const [routeFuelDialogOpen, setRouteFuelDialogOpen] = useState(false);

  const today = dayjs();
  const isCurrentMonth = currentMonth.isSame(today, "month");
  const todayDayOfMonth = today.date();

  const daysInMonth = currentMonth.daysInMonth();
  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, index) => index + 1),
    [daysInMonth],
  );

  const dispatcherFilter =
    selectedDispatcherId === "all" ? undefined : selectedDispatcherId;

  const isOperationsManager = role?.includes("OperationsManager");

  const monthKey = currentMonth.format("YYYY-MM-01");
  const monthStartIso = dayjs
    .utc(monthKey, "YYYY-MM-DD")
    .startOf("day")
    .toISOString();
  const monthEndIso = dayjs
    .utc(monthKey, "YYYY-MM-DD")
    .endOf("month")
    .endOf("day")
    .toISOString();

  const monthlyTargets = useMemo(
    () => findTargetsForMonth(currentMonth),
    [currentMonth],
  );

  const {
    data: assignmentsData,
    isLoading: assignmentsLoading,
    error: assignmentsError,
  } = useQuery({
    queryKey: ["operationsPlanner", token],
    queryFn: () => fetchPlannerAssignments(token),
    enabled: !!token,
  });

  const {
    data: plannerEntries = [],
    isLoading: entriesLoading,
    error: entriesError,
  } = useQuery({
    queryKey: [
      "operationsPlannerEntries",
      token,
      monthStartIso,
      monthEndIso,
      dispatcherFilter ?? "all",
    ],
    queryFn: () =>
      fetchPlannerEntries(token, monthStartIso, monthEndIso, dispatcherFilter),
    enabled: !!token,
  });

  const commentDialogQuery = useQuery({
    queryKey: [
      "plannerEntityComment",
      commentDialogState?.entityType ?? "none",
      commentDialogState?.entityId ?? "none",
    ],
    queryFn: () =>
      fetchPlannerEntityComment(
        token,
        commentDialogState?.entityId as string,
        commentDialogState?.entityType as PlannerEntityType,
      ),
    enabled: Boolean(
      token &&
      commentDialogState?.entityId &&
      commentDialogState.hasExistingComment &&
      commentDialogState.initialComment === undefined,
    ),
    staleTime: 5 * 60 * 1000,
  });

  const plannerRows = useMemo(() => {
    if (!assignmentsData || assignmentsData.length === 0) return [];

    const grouped = assignmentsData.reduce<Record<string, PlannerRow[]>>(
      (acc, entry) => {
        const dispatcherId = entry.dispatcherId ?? "unassigned";
        const dispatcherName = buildDispatcherName(
          entry.dispatcherNickname,
          entry.dispatcherFirstName,
          entry.dispatcherLastName,
        );

        const driverId =
          entry.driver?.id ??
          `${dispatcherId}-driver-${entry.driver?.firstName ?? "unknown"}-${
            entry.driver?.lastName ?? ""
          }`;
        const driverName =
          `${entry.driver?.firstName ?? ""} ${
            entry.driver?.lastName ?? ""
          }`.trim() || "Unassigned Driver";
        const hasTrailerDetails = Boolean(
          entry.trailer?.unitNumber ||
          entry.trailer?.trailerType ||
          entry.trailer?.type ||
          entry.trailer?.id,
        );

        const trailerType = hasTrailerDetails
          ? (entry.trailer?.trailerType ?? entry.trailer?.type ?? "N/A")
          : "Power Only";
        const trailerCategory = getTrailerCategory(trailerType);
        const dispatcherWorkPhoneNumber =
          entry.dispatcherWorkPhoneNumber ?? null;

        const driverDetails: PlannerRow = {
          dispatcherId,
          dispatcherName,
          dispatcherWorkPhoneNumber,
          driverId,
          driverName,
          companyName: entry.driver?.company ?? "N/A",
          phoneNumber: entry.driver?.phoneNumber ?? "N/A",
          driverType:
            entry.driver?.driverType ?? entry.driver?.type ?? "Not Provided",
          truckUnitNumber:
            entry.truck?.unitNumber ?? entry.truck?.truckNumber ?? "N/A",
          trailerUnitNumber: hasTrailerDetails
            ? (entry.trailer?.unitNumber ?? "N/A")
            : "PO",
          trailerType,
          trailerCategory,
          truckId: entry.truck?.id ?? undefined,
          trailerId: entry.trailer?.id ?? undefined,
          driverHasPlannerComment: entry.driverHasPlannerComment ?? false,
          truckHasPlannerComment: entry.truckHasPlannerComment ?? false,
          trailerHasPlannerComment: entry.trailerHasPlannerComment ?? false,
          isFirstDriver: false,
        };

        if (!acc[dispatcherId]) {
          acc[dispatcherId] = [];
        }

        acc[dispatcherId].push(driverDetails);
        return acc;
      },
      {},
    );

    const rows: PlannerRow[] = [];
    Object.values(grouped)
      .sort((a, b) => {
        const nameA = a[0]?.dispatcherName ?? "";
        const nameB = b[0]?.dispatcherName ?? "";
        return nameA.localeCompare(nameB);
      })
      .forEach((group) => {
        group.forEach((row, index) =>
          rows.push({ ...row, isFirstDriver: index === 0 }),
        );
      });

    return rows;
  }, [assignmentsData]);

  const dispatchers = useMemo(() => {
    const uniqueDispatchers = new Map<string, string>();
    assignmentsData?.forEach((entry) => {
      const dispatcherId = entry.dispatcherId ?? "unassigned";
      const dispatcherName = buildDispatcherName(
        entry.dispatcherNickname,
        entry.dispatcherFirstName,
        entry.dispatcherLastName,
      );
      uniqueDispatchers.set(dispatcherId, dispatcherName);
    });

    return Array.from(uniqueDispatchers.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [assignmentsData]);

  const filteredPlannerRows = useMemo(() => {
    let rows = plannerRows;
    const normalizedTruckUnitSearch = truckUnitSearch.trim().toLowerCase();

    if (selectedDispatcherId !== "all") {
      rows = rows.filter((row) => row.dispatcherId === selectedDispatcherId);
    }

    if (selectedTrailerCategory !== "all") {
      rows = rows.filter(
        (row) => row.trailerCategory === selectedTrailerCategory,
      );
    }

    if (plannerAssetFilter.assetType && plannerAssetFilter.assetId) {
      rows = rows.filter((row) =>
        plannerAssetFilter.assetType === "truck"
          ? row.truckId === plannerAssetFilter.assetId
          : row.trailerId === plannerAssetFilter.assetId,
      );
    }

    if (normalizedTruckUnitSearch) {
      rows = rows.filter((row) =>
        row.truckUnitNumber.toLowerCase().includes(normalizedTruckUnitSearch),
      );
    }

    return rows;
  }, [
    plannerRows,
    selectedDispatcherId,
    selectedTrailerCategory,
    truckUnitSearch,
    plannerAssetFilter.assetId,
    plannerAssetFilter.assetType,
  ]);

  const plannerRowsByViewMode = useMemo(() => {
    if (plannerViewMode === "default") {
      return filteredPlannerRows;
    }

    const uniqueRows = new Map<string, PlannerRow>();

    filteredPlannerRows.forEach((row) => {
      if (plannerViewMode === "driver") {
        uniqueRows.set(row.driverId, row);
        return;
      }

      if (plannerViewMode === "truck") {
        const truckKey = row.truckId ?? row.truckUnitNumber;
        if (!truckKey || truckKey === "N/A") {
          return;
        }
        uniqueRows.set(truckKey, row);
        return;
      }

      const trailerKey = row.trailerId ?? row.trailerUnitNumber;
      if (!trailerKey || trailerKey === "N/A") {
        return;
      }
      uniqueRows.set(trailerKey, row);
    });

    return Array.from(uniqueRows.values()).map((row, index) => ({
      ...row,
      isFirstDriver: index === 0,
    }));
  }, [filteredPlannerRows, plannerViewMode]);

  const totalTrucksByCategory = useMemo(() => {
    const categoryToTrucks = new Map<string, Set<string>>();

    plannerRows.forEach((row) => {
      const targetCategory = getTargetCategoryFromTrailerType(row.trailerType);
      if (!targetCategory) return;

      const truckKey = row.truckId ?? row.truckUnitNumber;
      if (!truckKey) return;

      const trucks = categoryToTrucks.get(targetCategory) ?? new Set<string>();
      trucks.add(truckKey);
      categoryToTrucks.set(targetCategory, trucks);
    });

    const result: Record<string, number> = {};
    categoryToTrucks.forEach((trucks, category) => {
      result[category] = trucks.size;
    });

    return result;
  }, [plannerRows]);

  const isFilterActive = selectedDispatcherId !== "all";

  const entriesByKey = useMemo(() => {
    const map = new Map<string, OperationsPlannerEntry>();
    plannerEntries.forEach((entry) => {
      if (!entry.driverId) return;
      const dateKey = getPlannerDateKey(entry.date);
      const key = `${entry.driverId}-${dateKey}`;
      const existing = map.get(key);

      const isDifferentPair = Boolean(
        entry.differentDriver ||
        entry.differentTruck ||
        entry.differentTrailer ||
        entry.existingPlannerEntryId,
      );

      if (!existing) {
        map.set(key, entry);
        return;
      }

      const existingIsDifferentPair = Boolean(
        existing.differentDriver ||
        existing.differentTruck ||
        existing.differentTrailer ||
        existing.existingPlannerEntryId,
      );

      if (existingIsDifferentPair && !isDifferentPair) {
        map.set(key, entry);
      } else if (!existingIsDifferentPair && isDifferentPair) {
        return;
      } else {
        map.set(key, entry);
      }
    });
    return map;
  }, [plannerEntries]);

  const entriesById = useMemo(() => {
    const map = new Map<string, OperationsPlannerEntry>();
    plannerEntries.forEach((entry) => {
      map.set(entry.id, entry);
    });
    return map;
  }, [plannerEntries]);

  const entriesByTruckAndDate = useMemo(() => {
    const map = new Map<string, OperationsPlannerEntry[]>();
    plannerEntries.forEach((entry) => {
      if (!entry.truckId || entry.isMonthlyTotal) return;
      const dateKey = getPlannerDateKey(entry.date);
      const key = `${entry.truckId}-${dateKey}`;
      const existing = map.get(key) ?? [];
      existing.push(entry);
      map.set(key, existing);
    });
    return map;
  }, [plannerEntries]);

  const entriesByTrailerAndDate = useMemo(() => {
    const map = new Map<string, OperationsPlannerEntry[]>();
    plannerEntries.forEach((entry) => {
      if (!entry.trailerId || entry.isMonthlyTotal) return;
      const dateKey = getPlannerDateKey(entry.date);
      const key = `${entry.trailerId}-${dateKey}`;
      const existing = map.get(key) ?? [];
      existing.push(entry);
      map.set(key, existing);
    });
    return map;
  }, [plannerEntries]);

  const hasDifferentPairMetadata = useCallback(
    (entry?: OperationsPlannerEntry) =>
      Boolean(
        entry &&
        (entry.differentDriver ||
          entry.differentTruck ||
          entry.differentTrailer ||
          entry.existingPlannerEntryId),
      ),
    [],
  );

  const resolveConflictSourceEntry = useCallback(
    (entry?: OperationsPlannerEntry) => {
      if (!entry) {
        return { entry: undefined, isDifferentPairEntry: false };
      }

      if (!hasDifferentPairMetadata(entry)) {
        return { entry, isDifferentPairEntry: false };
      }

      const referencedEntry = entry.existingPlannerEntryId
        ? entriesById.get(entry.existingPlannerEntryId)
        : undefined;

      return {
        entry: referencedEntry ?? entry,
        isDifferentPairEntry: true,
      };
    },
    [entriesById, hasDifferentPairMetadata],
  );

  const getDayEntryForRow = useCallback(
    (
      row: PlannerRow,
      dateKey: string,
    ): {
      entry: OperationsPlannerEntry | undefined;
      isDifferentPairEntry: boolean;
      conflictEntry?: OperationsPlannerEntry;
    } => {
      if (plannerViewMode === "driver") {
        return {
          entry: entriesByKey.get(`${row.driverId}-${dateKey}`),
          isDifferentPairEntry: false,
        };
      }

      if (plannerViewMode === "truck") {
        if (!row.truckId) {
          return { entry: undefined, isDifferentPairEntry: false };
        }

        const entry = entriesByTruckAndDate.get(
          `${row.truckId}-${dateKey}`,
        )?.[0];
        return { entry, isDifferentPairEntry: false };
      }

      if (plannerViewMode === "trailer") {
        if (!row.trailerId) {
          return { entry: undefined, isDifferentPairEntry: false };
        }

        const entry = entriesByTrailerAndDate.get(
          `${row.trailerId}-${dateKey}`,
        )?.[0];
        return { entry, isDifferentPairEntry: false };
      }

      const directEntry = entriesByKey.get(`${row.driverId}-${dateKey}`);

      const truckEntries = row.truckId
        ? entriesByTruckAndDate.get(`${row.truckId}-${dateKey}`)
        : undefined;

      const pairMismatchEntry = truckEntries?.find(
        (entry) =>
          hasDifferentPairMetadata(entry) && entry.id !== directEntry?.id,
      );

      if (directEntry) {
        const directResolved = resolveConflictSourceEntry(directEntry);

        if (pairMismatchEntry) {
          const conflictResolved =
            resolveConflictSourceEntry(pairMismatchEntry);
          if (
            conflictResolved.entry &&
            directResolved.entry &&
            conflictResolved.entry.id === directResolved.entry.id
          ) {
            return {
              entry: directResolved.entry,
              isDifferentPairEntry: directResolved.isDifferentPairEntry,
            };
          }
          return {
            entry: directResolved.entry,
            isDifferentPairEntry: false,
            conflictEntry: conflictResolved.entry,
          };
        }

        return directResolved;
      }

      if (!row.truckId || !truckEntries?.length) {
        return { entry: undefined, isDifferentPairEntry: false };
      }

      if (pairMismatchEntry) {
        return resolveConflictSourceEntry(pairMismatchEntry);
      }

      return { entry: undefined, isDifferentPairEntry: false };
    },
    [
      plannerViewMode,
      entriesByKey,
      entriesByTruckAndDate,
      entriesByTrailerAndDate,
      hasDifferentPairMetadata,
      resolveConflictSourceEntry,
    ],
  );

  const monthlySummariesByDriver = useMemo(() => {
    const map = new Map<string, OperationsPlannerEntry>();
    plannerEntries.forEach((entry) => {
      if (entry.driverId && entry.isMonthlyTotal) {
        map.set(entry.driverId, entry);
      }
    });
    return map;
  }, [plannerEntries]);

  useEffect(() => {
    if (!editorState) {
      setFormValues(createEmptyFormState());
      setFormError(null);
      return;
    }

    const { row, entry, date } = editorState;
    const resolvedDate = entry?.date
      ? formatPlannerDate(entry.date)
      : formatPlannerDate(date);
    setFormValues({
      id: entry?.id,
      date: resolvedDate,
      dispatcherId:
        row.dispatcherId === "unassigned" ? undefined : row.dispatcherId,
      driverId: row.driverId,
      driverName: row.driverName,
      truckId: entry?.truckId ?? row.truckId,
      trailerId: entry?.trailerId ?? row.trailerId,
      truckUnitNumber: entry?.truckUnitNumber ?? row.truckUnitNumber ?? "",
      trailerUnitNumber:
        entry?.trailerUnitNumber ?? row.trailerUnitNumber ?? "",
      gross:
        entry?.gross !== undefined && entry?.gross !== null
          ? String(entry.gross)
          : "",
      mileage:
        entry?.mileage !== undefined && entry?.mileage !== null
          ? String(entry.mileage)
          : "",
      noLoadBooked: entry?.noLoadBooked ?? false,
      rcReceivedButNotLoaded: entry?.rcReceivedButNotLoaded ?? false,
      loadPickedUp: entry?.loadPickedUp ?? false,
      inTransit: entry?.inTransit ?? false,
      delivered: entry?.delivered ?? false,
      emptyOrStuck: entry?.emptyOrStuck ?? false,
      emptyOrStuckNote: entry?.emptyOrStuckNote ?? "",
      shopOrYard: entry?.shopOrYard ?? false,
      hometimeOrOff: entry?.hometimeOrOff ?? false,
      pickupLocation: entry?.pickupLocation ?? "",
      brokerName: entry?.brokerName ?? "",
      brokerEmail: entry?.brokerEmail ?? "",
      brokerMcNumber: entry?.brokerMcNumber ?? "",
      brokerDotNumber: entry?.brokerDotNumber ?? "",
      selectedDates: [resolvedDate],
    });
    setFormError(null);
  }, [editorState]);

  useEffect(() => {
    if (!commentDialogState) {
      setCommentText("");
      setCommentError(null);
      return;
    }

    if (commentDialogState.initialComment !== undefined) {
      setCommentText(commentDialogState.initialComment ?? "");
      setCommentError(null);
      return;
    }

    const cachedComment =
      queryClient.getQueryData<PlannerEntityCommentDto | null>([
        "plannerEntityComment",
        commentDialogState.entityType,
        commentDialogState.entityId,
      ]);

    if (cachedComment?.comment) {
      setCommentText(cachedComment.comment);
    } else {
      setCommentText("");
    }
    setCommentError(null);
  }, [commentDialogState, queryClient]);

  useEffect(() => {
    if (commentDialogQuery.data && commentDialogState) {
      setCommentText((prev) => {
        if (commentDialogState.initialComment !== undefined && prev) {
          return prev;
        }

        if (prev.trim().length > 0) {
          return prev;
        }

        return commentDialogQuery.data?.comment ?? "";
      });
    }
  }, [commentDialogQuery.data, commentDialogState]);

  const visibleFixedColumns = useMemo(() => {
    const hiddenColumnsByViewMode: Record<PlannerViewMode, Set<string>> = {
      default: new Set(),
      driver: new Set(["dispatcher", "truck", "trailer"]),
      truck: new Set([
        "dispatcher",
        "driver",
        "company",
        "phone",
        "driverType",
      ]),
      trailer: new Set([
        "dispatcher",
        "driver",
        "company",
        "phone",
        "driverType",
        "truck",
      ]),
    };

    let baseColumns = FIXED_COLUMNS.filter(
      (column) => !hiddenColumnsByViewMode[plannerViewMode].has(column.key),
    );

    if (isFilterActive) {
      baseColumns = baseColumns.filter((column) => column.key !== "dispatcher");
    }

    if (showDetailsColumns) {
      return baseColumns;
    }

    if (compactPlanner) {
      const key =
        plannerViewMode === "truck"
          ? "truck"
          : plannerViewMode === "trailer"
            ? "trailer"
            : "driver";
      return baseColumns
        .filter((column) => column.key === key)
        .map((column) => ({ ...column, width: 150 }));
    }

    const columnsToKeep = new Set(VIEW_MODE_COLUMN_KEYS[plannerViewMode]);

    if (plannerViewMode === "default") {
      MANDATORY_COLUMN_KEYS.forEach((columnKey) => {
        columnsToKeep.add(columnKey);
      });
    }

    if (!isFilterActive && plannerViewMode === "default") {
      columnsToKeep.add("dispatcher");
    }

    return baseColumns.filter((column) => columnsToKeep.has(column.key));
  }, [compactPlanner, isFilterActive, plannerViewMode, showDetailsColumns]);

  const fixedColumnSignature = useMemo(
    () => visibleFixedColumns.map((column) => column.key).join("-"),
    [visibleFixedColumns],
  );

  const headerBackground = useMemo(
    () =>
      `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.12,
      )}, ${alpha(theme.palette.secondary.main, 0.12)})`,
    [theme],
  );

  const weekendHeaderBackground = useMemo(
    () => alpha(theme.palette.error.light, 0.16),
    [theme],
  );

  const weekendDividerBorder = useMemo(
    () => `2px solid ${alpha(theme.palette.error.main, 0.6)}`,
    [theme],
  );

  const neutralStatusTokens = useMemo(
    () => ({
      bg: theme.palette.common.white,
      borderColor: alpha(theme.palette.divider, 0.2),
    }),
    [theme],
  );

  const statusTokens = useMemo<
    Record<PlannerStatusKey, { bg: string; borderColor: string }>
  >(
    () => ({
      noLoad: {
        bg: theme.palette.common.white,
        borderColor: alpha(theme.palette.divider, 0.2),
      },
      rcReceived: {
        bg: alpha(theme.palette.warning.light, 0.25),
        borderColor: alpha(theme.palette.warning.main, 0.4),
      },
      loadPickedUp: {
        bg: alpha(theme.palette.success.light, 0.25),
        borderColor: alpha(theme.palette.success.main, 0.45),
      },
      inTransit: {
        bg: alpha(theme.palette.primary.light, 0.25),
        borderColor: alpha(theme.palette.primary.main, 0.45),
      },
      delivered: {
        bg: alpha(theme.palette.info.light, 0.24),
        borderColor: alpha(theme.palette.info.main, 0.45),
      },
      emptyOrStuck: {
        bg: alpha(theme.palette.error.light, 0.25),
        borderColor: alpha(theme.palette.error.main, 0.45),
      },
      shopOrYard: {
        bg: alpha("#9c27b0", 0.18),
        borderColor: alpha("#6a0080", 0.45),
      },
      hometimeOff: {
        bg: alpha(theme.palette.grey[500], 0.2),
        borderColor: alpha(theme.palette.grey[600], 0.35),
      },
    }),
    [theme],
  );

  const statusLegendTokens = useMemo<
    Record<
      PlannerStatusKey,
      { label: string; background: string; borderColor: string }
    >
  >(
    () => ({
      noLoad: {
        label: STATUS_LABEL_MAP.noLoad,
        background: statusTokens.noLoad.bg,
        borderColor: statusTokens.noLoad.borderColor,
      },
      rcReceived: {
        label: STATUS_LABEL_MAP.rcReceived,
        background: statusTokens.rcReceived.bg,
        borderColor: statusTokens.rcReceived.borderColor,
      },
      loadPickedUp: {
        label: STATUS_LABEL_MAP.loadPickedUp,
        background: statusTokens.loadPickedUp.bg,
        borderColor: statusTokens.loadPickedUp.borderColor,
      },
      inTransit: {
        label: STATUS_LABEL_MAP.inTransit,
        background: statusTokens.inTransit.bg,
        borderColor: statusTokens.inTransit.borderColor,
      },
      delivered: {
        label: STATUS_LABEL_MAP.delivered,
        background: statusTokens.delivered.bg,
        borderColor: statusTokens.delivered.borderColor,
      },
      emptyOrStuck: {
        label: STATUS_LABEL_MAP.emptyOrStuck,
        background: statusTokens.emptyOrStuck.bg,
        borderColor: statusTokens.emptyOrStuck.borderColor,
      },
      shopOrYard: {
        label: STATUS_LABEL_MAP.shopOrYard,
        background: statusTokens.shopOrYard.bg,
        borderColor: statusTokens.shopOrYard.borderColor,
      },
      hometimeOff: {
        label: STATUS_LABEL_MAP.hometimeOff,
        background: statusTokens.hometimeOff.bg,
        borderColor: statusTokens.hometimeOff.borderColor,
      },
    }),
    [statusTokens],
  );

  const grossFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const todayKey = useMemo(() => formatPlannerDate(today), [today]);

  const todaysEntries = useMemo(() => {
    return plannerRowsByViewMode.reduce<OperationsPlannerEntry[]>(
      (acc, row) => {
        const todaysEntry = getDayEntryForRow(row, todayKey).entry;

        if (!todaysEntry) {
          return acc;
        }

        if (
          selectedDispatcherId !== "all" &&
          todaysEntry.dispatcherId !== selectedDispatcherId
        ) {
          return acc;
        }

        acc.push(todaysEntry);
        return acc;
      },
      [],
    );
  }, [
    plannerRowsByViewMode,
    getDayEntryForRow,
    selectedDispatcherId,
    todayKey,
  ]);

  const statusCounts = useMemo(() => {
    const initialCounts = STATUS_OPTIONS.reduce(
      (acc, { key }) => ({ ...acc, [key]: 0 }),
      {} as Record<PlannerStatusKey, number>,
    );

    return plannerRowsByViewMode.reduce((acc, row) => {
      const entry = getDayEntryForRow(row, todayKey).entry;

      if (
        !entry ||
        (selectedDispatcherId !== "all" &&
          entry.dispatcherId !== selectedDispatcherId)
      ) {
        acc.noLoad += 1;
        return acc;
      }

      const hasNonNoLoadStatus = STATUS_FIELDS.some((field) => {
        if (field === "noLoadBooked") return false;
        return Boolean(entry[field as keyof OperationsPlannerEntry]);
      });

      if (entry.noLoadBooked || !hasNonNoLoadStatus) {
        acc.noLoad += 1;
        return acc;
      }

      const statusKey = getStatusKeyFromEntry(entry);
      if (statusKey) {
        acc[statusKey] += 1;
      }

      return acc;
    }, initialCounts);
  }, [
    plannerRowsByViewMode,
    getDayEntryForRow,
    selectedDispatcherId,
    todayKey,
  ]);

  const todaysGrossTotal = useMemo(
    () =>
      todaysEntries.reduce((sum, entry) => {
        if (entry.gross === null || entry.gross === undefined) {
          return sum;
        }

        return sum + Number(entry.gross);
      }, 0),
    [todaysEntries],
  );

  const rpmFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, "month"));
  };

  const handleDayCellClick = useCallback(
    (
      row: PlannerRow,
      day: number,
      entry?: OperationsPlannerEntry,
      isDifferentPairEntry = false,
    ) => {
      setEditorState({
        row,
        day,
        date: currentMonth.date(day),
        entry,
        isDifferentPairEntry,
      });
    },
    [currentMonth],
  );

  const handleDayCellKeyDown = useCallback(
    (
      row: PlannerRow,
      day: number,
      entry?: OperationsPlannerEntry,
      isDifferentPairEntry = false,
    ) =>
      (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleDayCellClick(row, day, entry, isDifferentPairEntry);
        }
      },
    [handleDayCellClick],
  );

  const handleTextFieldChange = useCallback(
    (field: keyof PlannerEntryForm) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;
        setFormValues((prev) => ({ ...prev, [field]: value }));
      },
    [],
  );

  const handleCheckboxChange = useCallback(
    (field: keyof PlannerEntryForm) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setFormValues((prev) => {
          const updated: PlannerEntryForm = { ...prev, [field]: checked };

          if (checked && STATUS_FIELDS.includes(field)) {
            STATUS_FIELDS.forEach((statusField) => {
              if (statusField !== field) {
                (updated as any)[statusField] = false;
              }
            });
          }

          return updated;
        });
      },
    [],
  );

  const handleSelectedDatesChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value;
      const nextSelection = (
        typeof value === "string"
          ? value.split(",")
          : Array.isArray(value)
            ? (value as string[])
            : []
      ).map((date) => formatPlannerDate(date));

      setFormValues((prev) => {
        const baseDate = prev.date || editorState?.date?.format("YYYY-MM-DD");
        const merged = new Set<string>(nextSelection);
        if (baseDate) {
          merged.add(baseDate);
        }

        return { ...prev, selectedDates: Array.from(merged) };
      });
    },
    [editorState?.date],
  );

  const saveCommentMutation = useMutation<
    PlannerEntityCommentDto,
    Error,
    PlannerEntityCommentDto
  >({
    mutationFn: async (payload: PlannerEntityCommentDto) => {
      if (!token) {
        throw new Error("Authentication is required");
      }

      const normalizedComment = payload.comment?.trim() ?? null;

      return setPlannerEntityComment(token, {
        ...payload,
        comment:
          normalizedComment && normalizedComment.length > 0
            ? normalizedComment
            : null,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["operationsPlanner", token] });
      queryClient.invalidateQueries({ queryKey: ["operationsPlannerEntries"] });
      queryClient.invalidateQueries({ queryKey: ["plannerEntityComment"] });
      queryClient.setQueryData<PlannerEntityCommentDto>(
        ["plannerEntityComment", data.entityType, data.entityId],
        data,
      );
      setCommentDialogState(null);
      setCommentError(null);
    },
    onError: (mutationError) => {
      setCommentError(
        mutationError instanceof Error
          ? mutationError.message
          : "Unable to save comment",
      );
    },
  });

  const saveEntryMutation = useMutation<
    OperationsPlannerEntry[],
    Error,
    PlannerEntryForm
  >({
    mutationFn: async (form: PlannerEntryForm) => {
      if (!token) {
        throw new Error("Authentication is required");
      }

      const grossValue = form.gross.trim();
      const parsedGross = grossValue ? Number(grossValue) : null;
      const normalizedGross =
        parsedGross !== null && !Number.isNaN(parsedGross) ? parsedGross : null;

      const mileageValue = form.mileage.trim();
      const parsedMileage = mileageValue ? Number(mileageValue) : null;
      const normalizedMileage =
        parsedMileage !== null && !Number.isNaN(parsedMileage)
          ? parsedMileage
          : null;

      const selectedDates = form.selectedDates.length
        ? form.selectedDates
        : form.date
          ? [form.date]
          : [];

      const uniqueDates = Array.from(new Set(selectedDates));
      if (!uniqueDates.length) {
        throw new Error("Please select at least one date to save.");
      }

      const results = await Promise.all(
        uniqueDates.map((selectedDate) => {
          const dateKey = formatPlannerDate(selectedDate);
          const existingEntry = entriesByKey.get(`${form.driverId}-${dateKey}`);

          const payload: OperationsPlannerEntryPayload = {
            id:
              existingEntry?.id ??
              (dateKey === form.date ? form.id : undefined),
            date: serializePlannerDate(dateKey),
            dispatcherId: form.dispatcherId,
            driverId: form.driverId || undefined,
            driverName: form.driverName || undefined,
            truckId: form.truckId || null,
            truckUnitNumber: form.truckUnitNumber.trim() || undefined,
            trailerId: form.trailerId || null,
            trailerUnitNumber: form.trailerUnitNumber.trim() || undefined,
            gross: normalizedGross,
            mileage: normalizedMileage,
            noLoadBooked: form.noLoadBooked,
            rcReceivedButNotLoaded: form.rcReceivedButNotLoaded,
            loadPickedUp: form.loadPickedUp,
            inTransit: form.inTransit,
            delivered: form.delivered,
            emptyOrStuck: form.emptyOrStuck,
            emptyOrStuckNote: form.emptyOrStuckNote.trim() || undefined,
            shopOrYard: form.shopOrYard,
            hometimeOrOff: form.hometimeOrOff,
            pickupLocation: form.pickupLocation.trim() || undefined,
            brokerName: form.brokerName.trim() || undefined,
            brokerEmail: form.brokerEmail.trim() || undefined,
            brokerMcNumber: form.brokerMcNumber.trim() || undefined,
            brokerDotNumber: form.brokerDotNumber.trim() || undefined,
          };

          return payload.id
            ? updatePlannerEntry(token, payload)
            : createPlannerEntry(token, payload);
        }),
      );

      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["operationsPlannerEntries"],
      });
      setFormError(null);
      setEditorState(null);
    },
    onError: (mutationError) => {
      setFormError(
        mutationError instanceof Error
          ? mutationError.message
          : "Unable to save entry",
      );
    },
  });

  const handleDialogClose = useCallback(() => {
    if (saveEntryMutation.isPending) return;
    setEditorState(null);
  }, [saveEntryMutation.isPending]);

  const pickupLocationError =
    formValues.loadPickedUp && !formValues.pickupLocation.trim();
  const emptyOrStuckNoteError =
    formValues.emptyOrStuck && !formValues.emptyOrStuckNote.trim();
  const selectedDates = formValues.selectedDates.length
    ? formValues.selectedDates
    : formValues.date
      ? [formValues.date]
      : [];

  const isDifferentPairReadonly = Boolean(editorState?.isDifferentPairEntry);
  const isViewModeReadonly = plannerViewMode !== "default";
  const isEditorReadonly = isDifferentPairReadonly || isViewModeReadonly;

  const handleCommentDialogClose = () => {
    if (saveCommentMutation.isPending) return;
    setCommentDialogState(null);
  };

  const handleCommentSave = () => {
    if (!commentDialogState) return;
    setCommentError(null);
    saveCommentMutation.mutate({
      entityId: commentDialogState.entityId,
      entityType: commentDialogState.entityType,
      comment: commentText,
    });
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editorState) return;
    if (brokerCheckPending) return;

    if (isDifferentPairReadonly) {
      setFormError(
        "This day already belongs to a different truck/driver/trailer pair and is read-only.",
      );
      return;
    }

    if (isViewModeReadonly) {
      setFormError("Switch to Default view mode to edit planner entries.");
      return;
    }

    if (pickupLocationError) {
      setFormError(
        "Pickup location is required when Load Picked Up is selected.",
      );
      return;
    }

    if (formValues.loadPickedUp) {
      const grossMissing = !formValues.gross.trim();
      const mileageMissing = !formValues.mileage.trim();

      if (grossMissing || mileageMissing) {
        setFormError(
          "Gross and Mileage are required when Load Picked Up is selected.",
        );
        return;
      }
    }

    if (emptyOrStuckNoteError) {
      setFormError("Please provide a reason when marking Empty or Stuck.");
      return;
    }

    setFormError(null);
    saveEntryMutation.mutate(formValues);
  };

  const getPairDifferencesLabel = (
    row: PlannerRow,
    entry?: OperationsPlannerEntry,
  ): string | null => {
    if (!entry) return null;

    const differences: string[] = [];

    if (entry.driverId && row.driverId && entry.driverId !== row.driverId) {
      differences.push("driver");
    }

    const rowTruckKey = row.truckId || row.truckUnitNumber;
    const entryTruckKey = entry.truckId || entry.truckUnitNumber;
    if (rowTruckKey && entryTruckKey && rowTruckKey !== entryTruckKey) {
      differences.push("truck");
    }

    const rowTrailerKey = row.trailerId || row.trailerUnitNumber;
    const entryTrailerKey = entry.trailerId || entry.trailerUnitNumber;
    if (rowTrailerKey && entryTrailerKey && rowTrailerKey !== entryTrailerKey) {
      differences.push("trailer");
    }

    if (!differences.length) return null;

    if (differences.length === 1) return differences[0];
    if (differences.length === 2) return differences.join(" and ");
    return `${differences[0]}, ${differences[1]} and ${differences[2]}`;
  };

  const buildDifferentPairTooltip = (
    row: PlannerRow,
    entry?: OperationsPlannerEntry,
  ): string => {
    if (!entry) {
      return "This entry belongs to a different truck/driver/trailer pairing and is read-only.";
    }

    const differencesLabel = getPairDifferencesLabel(row, entry);
    const header = differencesLabel
      ? `Different ${differencesLabel} (read-only).`
      : "Different truck/driver/trailer pairing (read-only).";

    const details: string[] = [];

    const driverDiff =
      (entry.driverId && row.driverId && entry.driverId !== row.driverId) ||
      (entry.driverName &&
        row.driverName &&
        entry.driverName !== row.driverName);
    if (driverDiff) {
      details.push(
        `Driver: ${entry.driverName ?? "N/A"} (entry) vs ${
          row.driverName || "N/A"
        } (current)`,
      );
    }

    const rowTruckKey = row.truckId || row.truckUnitNumber;
    const entryTruckKey = entry.truckId || entry.truckUnitNumber;
    if (rowTruckKey && entryTruckKey && rowTruckKey !== entryTruckKey) {
      details.push(
        `Truck #: ${entry.truckUnitNumber || "N/A"} (entry) vs ${
          row.truckUnitNumber || "N/A"
        } (current)`,
      );
    }

    const rowTrailerKey = row.trailerId || row.trailerUnitNumber;
    const entryTrailerKey = entry.trailerId || entry.trailerUnitNumber;
    if (rowTrailerKey && entryTrailerKey && rowTrailerKey !== entryTrailerKey) {
      details.push(
        `Trailer #: ${entry.trailerUnitNumber || "N/A"} (entry) vs ${
          row.trailerUnitNumber || "N/A"
        } (current)`,
      );
    }

    if (!details.length) {
      return header;
    }

    return `${header}\n${details.join("\n")}`;
  };

  const renderCell = useCallback<MultiGridProps["cellRenderer"]>(
    ({ columnIndex, key, rowIndex, style }) => {
      const fixedColumnCount = visibleFixedColumns.length;
      const calendarColumnIndex = columnIndex - fixedColumnCount;

      if (rowIndex === 0) {
        if (columnIndex < fixedColumnCount) {
          const column = visibleFixedColumns[columnIndex];
          return (
            <Box
              key={key}
              style={style}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                bgcolor: headerBackground,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                borderRight: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              {column.label}
            </Box>
          );
        }

        if (calendarColumnIndex < days.length) {
          const day = days[calendarColumnIndex];
          const date = currentMonth.date(day);
          const isToday = isCurrentMonth && day === todayDayOfMonth;
          const isWeekendDay = date.day() === 0 || date.day() === 6;
          const isSunday = date.day() === 0;
          const dayColumnBorder = isSunday
            ? weekendDividerBorder
            : `1px solid ${alpha(theme.palette.divider, 0.3)}`;
          return (
            <Box
              key={key}
              style={style}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isToday
                  ? alpha(theme.palette.primary.main, 0.18)
                  : isWeekendDay
                    ? weekendHeaderBackground
                    : headerBackground,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                borderRight: dayColumnBorder,
                fontWeight: 600,
              }}
            >
              <Typography
                fontSize={14}
                color={isWeekendDay ? theme.palette.error.dark : undefined}
              >
                {day}
              </Typography>
              <Typography
                fontSize={11}
                color={
                  isWeekendDay ? theme.palette.error.dark : "text.secondary"
                }
              >
                {date.format("ddd")}
              </Typography>
            </Box>
          );
        }

        const summaryColumn =
          SUMMARY_COLUMNS[calendarColumnIndex - days.length];
        return (
          <Box
            key={key}
            style={style}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: headerBackground,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              fontWeight: 600,
            }}
          >
            <Typography fontSize={13}>{summaryColumn.label}</Typography>
          </Box>
        );
      }

      const row = plannerRowsByViewMode[rowIndex - 1];
      if (!row) return null;

      if (columnIndex < fixedColumnCount) {
        const column = visibleFixedColumns[columnIndex];
        let value: string = "";

        switch (column.key) {
          case "dispatcher":
            value = row.isFirstDriver ? row.dispatcherName : "";
            break;
          case "driver":
            value = row.driverName;
            break;
          case "company":
            value = row.companyName;
            break;
          case "phone":
            value = row.phoneNumber;
            break;
          case "driverType":
            value = row.driverType;
            break;
          case "truck":
            value = row.truckUnitNumber;
            break;
          case "trailer":
            value = row.trailerUnitNumber;
            break;
          case "trailerType":
            value = camelCaseToSentenceCase(row.trailerType);
            break;
        }

        const displayValue = value || "-";
        const tooltipTitle =
          column.key === "dispatcher" ? row.dispatcherWorkPhoneNumber : value;
        const shouldShowTooltip =
          (column.key === "phone" && value && value !== "N/A") ||
          (column.key === "dispatcher" &&
            row.isFirstDriver &&
            Boolean(value) &&
            Boolean(row.dispatcherWorkPhoneNumber));
        const companyHighlight =
          column.key === "driver"
            ? getCompanyColor(row.companyName)
            : undefined;
        const baseBackground = row.isFirstDriver
          ? alpha(theme.palette.primary.light, 0.08)
          : "background.paper";
        const isCommentColumn =
          column.key === "driver" ||
          column.key === "truck" ||
          column.key === "trailer";

        let commentEntityId: string | undefined;
        let commentEntityType: PlannerEntityType | null = null;
        let commentLabel = displayValue;
        let hasComment = false;

        if (isCommentColumn) {
          if (column.key === "driver") {
            commentEntityId = row.driverId;
            commentEntityType = "Driver";
            commentLabel = row.driverName;
            hasComment = row.driverHasPlannerComment;
          } else if (column.key === "truck") {
            commentEntityId = row.truckId;
            commentEntityType = "Truck";
            commentLabel = `Truck ${row.truckUnitNumber || "N/A"}`;
            hasComment = row.truckHasPlannerComment;
          } else if (column.key === "trailer") {
            commentEntityId = row.trailerId;
            commentEntityType = "Trailer";
            commentLabel = `Trailer ${row.trailerUnitNumber || "N/A"}`;
            hasComment = row.trailerHasPlannerComment;
          }
        }

        return (
          <Box
            key={key}
            style={style}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              bgcolor: companyHighlight ?? baseBackground,
              fontWeight: column.key === "dispatcher" ? 600 : 400,
              position: "relative",
              pr: isCommentColumn ? 6 : 2,
            }}
          >
            {shouldShowTooltip ? (
              <Tooltip title={tooltipTitle} placement="top" arrow>
                <Typography
                  variant="body2"
                  color="text.primary"
                  noWrap
                  sx={{ width: "100%" }}
                >
                  {displayValue}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                variant="body2"
                color="text.primary"
                noWrap={!compactPlanner}
                sx={{
                  width: "100%",
                  fontSize: column.key === "driver" ? "0.95rem" : "0.875rem",
                  fontWeight: column.key === "driver" ? 600 : 400,
                }}
              >
                {displayValue}
                {compactPlanner && visibleFixedColumns.length === 1 && (
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      typography: "caption",
                      color: "text.secondary",
                      mt: 0.5,
                    }}
                  >
                    {row.dispatcherName}
                  </Box>
                )}
              </Typography>
            )}
            {isCommentColumn && commentEntityId && commentEntityType && (
              <PlannerCommentControls
                token={token}
                entityId={commentEntityId}
                entityType={commentEntityType}
                hasComment={hasComment}
                label={commentLabel}
                onEdit={({
                  entityId,
                  entityType,
                  label,
                  initialComment,
                  hasExistingComment,
                }) =>
                  setCommentDialogState({
                    entityId,
                    entityType,
                    label,
                    initialComment,
                    hasExistingComment,
                  })
                }
              />
            )}
          </Box>
        );
      }

      if (calendarColumnIndex >= days.length) {
        const summaryColumn =
          SUMMARY_COLUMNS[calendarColumnIndex - days.length];
        const monthlySummary = monthlySummariesByDriver.get(row.driverId);
        const monthlyLabel = `${row.driverName} - ${currentMonth.format(
          "MMMM YYYY",
        )}`;

        const targetCategory = getTargetCategoryFromTrailerType(
          row.trailerType,
        );
        const monthlyTarget = targetCategory
          ? monthlyTargets[targetCategory]
          : undefined;
        const totalTrucksForCategory = targetCategory
          ? (totalTrucksByCategory[targetCategory] ?? 0)
          : 0;

        let displayValue: string | undefined;
        if (summaryColumn.key === "monthlyTargetGross") {
          displayValue =
            monthlyTarget?.gross !== undefined && totalTrucksForCategory > 0
              ? grossFormatter.format(
                  monthlyTarget.gross / totalTrucksForCategory,
                )
              : undefined;
        } else if (summaryColumn.key === "monthlyGross") {
          displayValue =
            monthlySummary?.monthlyGross !== null &&
            monthlySummary?.monthlyGross !== undefined
              ? grossFormatter.format(monthlySummary.monthlyGross)
              : undefined;
        } else if (summaryColumn.key === "monthlyTargetRpm") {
          displayValue =
            monthlyTarget?.rpm !== undefined
              ? rpmFormatter.format(monthlyTarget.rpm)
              : undefined;
        } else if (summaryColumn.key === "monthlyRpm") {
          displayValue =
            monthlySummary?.monthlyRpm !== null &&
            monthlySummary?.monthlyRpm !== undefined
              ? rpmFormatter.format(monthlySummary.monthlyRpm)
              : undefined;
        } else {
          displayValue = monthlySummary?.monthlyComment?.trim() || undefined;
        }

        return (
          <Box
            key={key}
            style={style}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              bgcolor: "background.paper",
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              position: "relative",
              pr: summaryColumn.key === "monthlyComment" ? 6 : 1.5,
            }}
          >
            <Typography
              variant="body2"
              color="text.primary"
              noWrap
              sx={{ fontWeight: 600 }}
            >
              {displayValue ?? "-"}
            </Typography>
            {summaryColumn.key === "monthlyComment" && monthlySummary && (
              <PlannerCommentControls
                token={token}
                entityId={monthlySummary.id}
                entityType="OperationsPlannerEntry"
                hasComment={Boolean(monthlySummary.monthlyComment)}
                label={`${monthlyLabel} total`}
                onEdit={({ entityId, entityType, label }) =>
                  setCommentDialogState({
                    entityId,
                    entityType,
                    label,
                    initialComment: monthlySummary.monthlyComment ?? undefined,
                    hasExistingComment: Boolean(monthlySummary.monthlyComment),
                  })
                }
              />
            )}
          </Box>
        );
      }

      const day = days[calendarColumnIndex];
      const isToday = isCurrentMonth && day === todayDayOfMonth;
      const date = currentMonth.date(day);
      const isSunday = date.day() === 0;
      const dateKey = date.format("YYYY-MM-DD");
      const { entry, isDifferentPairEntry, conflictEntry } = getDayEntryForRow(
        row,
        dateKey,
      );

      const primaryStatusKey = getStatusKeyFromEntry(entry);
      const primaryStatusToken = primaryStatusKey
        ? statusTokens[primaryStatusKey]
        : neutralStatusTokens;

      const conflictStatusKey = getStatusKeyFromEntry(conflictEntry);
      const conflictStatusToken = conflictStatusKey
        ? statusTokens[conflictStatusKey]
        : neutralStatusTokens;
      const highlightShadow = isToday
        ? `, inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.45)}`
        : "";
      const ariaLabel = `${row.driverName} - ${date.format("MMMM D, YYYY")}`;

      const conflictDifferencesLabel = conflictEntry
        ? getPairDifferencesLabel(row, conflictEntry)
        : null;

      const twoEntriesTooltip = conflictEntry
        ? `Two entries for this day: top is the current driver record (editable), bottom is a record for a ${
            conflictDifferencesLabel
              ? `different ${conflictDifferencesLabel} pairing`
              : "different truck/driver/trailer pairing"
          } (read-only).`
        : null;

      const renderEntryContent = (
        activeEntry?: OperationsPlannerEntry,
        statusTokenOverride?: typeof primaryStatusToken,
        showDifferentPairBadge?: boolean,
        differentPairTooltip?: string | null,
      ) => {
        const activePickupLocation = activeEntry?.pickupLocation?.trim();
        const activeEmptyNote = activeEntry?.emptyOrStuckNote?.trim();
        const activeEmptyNoteTimestampChicago =
          activeEntry?.emptyOrStuckNoteTimestampChicago;
        const activeEmptyNoteTimestampDisplay =
          isOperationsManager && activeEmptyNoteTimestampChicago
            ? dayjs(activeEmptyNoteTimestampChicago).format(
                "MMM D, YYYY h:mm A",
              )
            : null;
        const activeEmptyNoteTooltip = activeEmptyNote ? (
          isOperationsManager && activeEmptyNoteTimestampDisplay ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="inherit">
                {activeEmptyNote}
              </Typography>
              <Typography variant="caption" color="inherit">
                Added: {activeEmptyNoteTimestampDisplay} CT
              </Typography>
            </Stack>
          ) : (
            activeEmptyNote
          )
        ) : undefined;

        const activeGrossDisplay =
          activeEntry?.gross !== undefined && activeEntry?.gross !== null
            ? grossFormatter.format(activeEntry.gross)
            : undefined;
        const isPreTripMade = Boolean(activeEntry?.isPreTripMade);
        const activeMileageDisplay =
          activeEntry?.mileage !== undefined && activeEntry?.mileage !== null
            ? grossFormatter.format(activeEntry.mileage)
            : undefined;
        const activeGrossMileageSummary = [
          activeGrossDisplay ? `$${activeGrossDisplay}` : undefined,
          activeMileageDisplay ? `${activeMileageDisplay} mi` : undefined,
        ]
          .filter(Boolean)
          .join(" • ");

        return (
          <>
            {activePickupLocation && (
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.primary"
                sx={{
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {activePickupLocation}
              </Typography>
            )}
            {(activeGrossDisplay || activeMileageDisplay) && (
              <Tooltip
                title={
                  <Stack spacing={0.25}>
                    {activeGrossDisplay && (
                      <Typography variant="caption" color="inherit">
                        Gross: ${activeGrossDisplay}
                      </Typography>
                    )}
                    {activeMileageDisplay && (
                      <Typography variant="caption" color="inherit">
                        Mileage: {activeMileageDisplay} mi
                      </Typography>
                    )}
                  </Stack>
                }
                placement="top"
                arrow
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ minWidth: 0 }}
                >
                  {activeGrossMileageSummary}
                </Typography>
              </Tooltip>
            )}
            {activeEntry && (
              <Tooltip
                title={
                  isPreTripMade
                    ? "Pre-trip inspection completed"
                    : "Pre-trip inspection not completed"
                }
                placement="top"
                arrow
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <MenuBookOutlinedIcon
                    fontSize="small"
                    sx={{
                      color: isPreTripMade
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                    }}
                  />
                </Stack>
              </Tooltip>
            )}
            {activeEmptyNote && (
              <Tooltip title={activeEmptyNoteTooltip} placement="top" arrow>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontStyle: "italic",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {activeEmptyNote}
                </Typography>
              </Tooltip>
            )}
            {showDifferentPairBadge && (
              <Tooltip
                title={
                  differentPairTooltip ??
                  buildDifferentPairTooltip(row, activeEntry)
                }
                placement="top"
                arrow
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <WarningAmberRoundedIcon color="warning" fontSize="small" />
                  <Typography variant="caption" color="warning.main" noWrap>
                    Different pair (read-only)
                  </Typography>
                </Stack>
              </Tooltip>
            )}
            {!activeEntry && (
              <Typography variant="caption" color="text.secondary">
                Click to add details
              </Typography>
            )}
          </>
        );
      };

      const conflictPairTooltip = conflictEntry
        ? buildDifferentPairTooltip(row, conflictEntry)
        : null;

      return (
        <Box key={key} style={style} sx={{ width: "100%", height: "100%" }}>
          {entry && conflictEntry ? (
            <Tooltip title={twoEntriesTooltip} placement="top" arrow>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderBottom: `1px solid ${alpha(
                    theme.palette.divider,
                    0.12,
                  )}`,
                  borderRight: isSunday
                    ? weekendDividerBorder
                    : `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={`${ariaLabel} - Driver entry`}
                  onClick={() => handleDayCellClick(row, day, entry, false)}
                  onKeyDown={handleDayCellKeyDown(row, day, entry, false)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.4,
                    px: 1,
                    py: 0.5,
                    overflow: "hidden",
                    border: "none",
                    m: 0,
                    backgroundColor: primaryStatusToken.bg,
                    boxShadow: `inset 0 0 0 1px ${primaryStatusToken.borderColor}${highlightShadow}`,
                    textAlign: "left",
                    cursor: "pointer",
                    "&:focus-visible": {
                      outline: `2px solid ${alpha(
                        theme.palette.primary.main,
                        0.7,
                      )}`,
                      outlineOffset: -2,
                    },
                  }}
                >
                  {renderEntryContent(entry, primaryStatusToken, false, null)}
                </Box>
                <Box
                  component="button"
                  type="button"
                  aria-label={`${ariaLabel} - Different pair entry`}
                  onClick={() =>
                    handleDayCellClick(row, day, conflictEntry, true)
                  }
                  onKeyDown={handleDayCellKeyDown(
                    row,
                    day,
                    conflictEntry,
                    true,
                  )}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.4,
                    px: 1,
                    py: 0.5,
                    overflow: "hidden",
                    border: "none",
                    m: 0,
                    backgroundColor: conflictStatusToken.bg,
                    boxShadow: `inset 0 0 0 1px ${conflictStatusToken.borderColor}`,
                    textAlign: "left",
                    cursor: "pointer",
                    borderTop: `1px dashed ${alpha(
                      theme.palette.warning.main,
                      0.6,
                    )}`,
                    "&:focus-visible": {
                      outline: `2px solid ${alpha(
                        theme.palette.primary.main,
                        0.7,
                      )}`,
                      outlineOffset: -2,
                    },
                  }}
                >
                  {renderEntryContent(
                    conflictEntry,
                    conflictStatusToken,
                    true,
                    conflictPairTooltip,
                  )}
                </Box>
              </Box>
            </Tooltip>
          ) : (
            <Box
              component="button"
              type="button"
              aria-label={ariaLabel}
              onClick={() =>
                handleDayCellClick(row, day, entry, isDifferentPairEntry)
              }
              onKeyDown={handleDayCellKeyDown(
                row,
                day,
                entry,
                isDifferentPairEntry,
              )}
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 0.4,
                px: 1,
                py: 0.75,
                overflow: "hidden",
                border: "none",
                m: 0,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                borderRight: isSunday
                  ? weekendDividerBorder
                  : `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                backgroundColor: primaryStatusToken.bg,
                boxShadow: `inset 0 0 0 1px ${primaryStatusToken.borderColor}${highlightShadow}`,
                textAlign: "left",
                cursor: "pointer",
                "&:focus-visible": {
                  outline: `2px solid ${alpha(
                    theme.palette.primary.main,
                    0.7,
                  )}`,
                  outlineOffset: -2,
                },
              }}
            >
              {renderEntryContent(
                entry,
                primaryStatusToken,
                isDifferentPairEntry,
                isDifferentPairEntry
                  ? `${buildDifferentPairTooltip(row, entry)} Click to review.`
                  : null,
              )}
            </Box>
          )}
        </Box>
      );
    },
    [
      visibleFixedColumns,
      compactPlanner,
      plannerRowsByViewMode,
      days,
      isCurrentMonth,
      todayDayOfMonth,
      currentMonth,
      getDayEntryForRow,
      statusTokens,
      neutralStatusTokens,
      isOperationsManager,
      grossFormatter,
      theme.palette.primary.main,
      theme.palette.primary.light,
      theme.palette.divider,
      theme.palette.error.main,
      theme.palette.error.dark,
      theme.palette.success.main,
      theme.palette.warning.main,
      handleDayCellKeyDown,
      weekendDividerBorder,
      headerBackground,
      weekendHeaderBackground,
      token,
      monthlySummariesByDriver,
      monthlyTargets,
      totalTrucksByCategory,
      rpmFormatter,
      handleDayCellClick,
      buildDifferentPairTooltip,
    ],
  );

  const scrollToTodayColumn = useMemo(() => {
    if (!isCurrentMonth) return undefined;
    return visibleFixedColumns.length + todayDayOfMonth - 1;
  }, [isCurrentMonth, todayDayOfMonth, visibleFixedColumns.length]);

  if (assignmentsLoading || entriesLoading) {
    return (
      <Box
        sx={{
          height: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (assignmentsError instanceof Error) {
    return <Alert severity="error">{assignmentsError.message}</Alert>;
  }

  if (entriesError instanceof Error) {
    return <Alert severity="error">{entriesError.message}</Alert>;
  }

  if (!plannerRows.length) {
    return (
      <Alert severity="info">
        No dispatcher assignments available for planning yet.
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: { xs: 0, sm: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={handlePrevMonth}>
            <ArrowBackIosNewRoundedIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="h6"
            fontWeight={600}
            minWidth={{ xs: 0, sm: 160 }}
            textAlign="center"
          >
            {currentMonth.format("MMMM YYYY")}
          </Typography>
          <IconButton onClick={handleNextMonth}>
            <ArrowForwardIosRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack
          direction={{ xs: "column", md: "row" }}
          flexWrap="wrap"
          useFlexGap
          spacing={2}
          width="100%"
          justifyContent="flex-end"
        >
          <CheckBrokerButton />
          <TextField
            select
            label="View mode"
            size="small"
            value={plannerViewMode}
            onChange={(event) =>
              setPlannerViewMode(event.target.value as PlannerViewMode)
            }
            sx={{ minWidth: { xs: 0, sm: 200 } }}
          >
            <MenuItem value="default">Default view</MenuItem>
            <MenuItem value="driver">View by driver</MenuItem>
            <MenuItem value="truck">View by truck</MenuItem>
            <MenuItem value="trailer">View by trailer</MenuItem>
          </TextField>
          <TextField
            select
            label="Dispatcher"
            size="small"
            value={selectedDispatcherId}
            onChange={(event) => setSelectedDispatcherId(event.target.value)}
            sx={{ minWidth: { xs: 0, sm: 220 } }}
          >
            <MenuItem value="all">All Dispatchers</MenuItem>
            {dispatchers.map((dispatcher) => (
              <MenuItem key={dispatcher.id} value={dispatcher.id}>
                {dispatcher.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Trailer Type"
            size="small"
            value={selectedTrailerCategory}
            onChange={(event) =>
              setSelectedTrailerCategory(
                event.target.value as TrailerCategoryFilter,
              )
            }
            sx={{ minWidth: { xs: 0, sm: 240 } }}
          >
            {TRAILER_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Truck unit #"
            size="small"
            value={truckUnitSearch}
            onChange={(event) => setTruckUnitSearch(event.target.value)}
            placeholder="Search truck unit"
            sx={{ minWidth: { xs: 0, sm: 200 } }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowDetailsColumns((prev) => !prev)}
          >
            {showDetailsColumns ? "Collapse details" : "Expand details"}
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <Typography color="text.secondary" variant="body2">
          {plannerViewMode === "default"
            ? isFilterActive
              ? "Dispatcher selection hides the Dispatcher column to provide more room for the calendar."
              : "Dispatcher overview is fixed on the left. Collapse details to focus on the calendar."
            : "View mode hides unrelated columns so you can focus on the selected planner perspective."}
        </Typography>
        <Stack
          direction="row"
          spacing={1.25}
          flexWrap="wrap"
          alignItems="center"
        >
          {STATUS_OPTIONS.map(({ key }) => {
            const legend = statusLegendTokens[key];
            return (
              <Stack
                key={key}
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.75,
                    bgcolor: legend.background,
                    border: `1px solid ${legend.borderColor}`,
                    flexShrink: 0,
                  }}
                />
                <Stack spacing={0.25}>
                  <Typography variant="caption" color="text.secondary">
                    {legend.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.primary"
                  >
                    {statusCounts[key] ?? 0}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              Total Gross (Today)
            </Typography>
            <Typography variant="caption" fontWeight={700} color="text.primary">
              ${grossFormatter.format(todaysGrossTotal)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {plannerAssetFilter.assetType && plannerAssetFilter.assetId && (
        <Alert severity={plannerRowsByViewMode.length ? "info" : "warning"}>
          {plannerRowsByViewMode.length
            ? `Filtered by ${plannerAssetFilter.assetType} ${
                plannerAssetFilter.unitNumber || plannerAssetFilter.assetId
              }.`
            : `No planner rows found for ${plannerAssetFilter.assetType} ${
                plannerAssetFilter.unitNumber || plannerAssetFilter.assetId
              } in the current month.`}
        </Alert>
      )}
      <Box
        sx={{
          height: {
            xs: 480,
            sm: 560,
            md: 640,
            lg: 1500,
          },
          maxHeight: "100vh",
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          overflow: "hidden",
          width: "100%",
          minWidth: 0,
        }}
        role="region"
        aria-label="Dispatcher planner"
      >
        <AutoSizer>
          {({ height, width }) => (
            <MultiGrid
              key={fixedColumnSignature}
              cellRenderer={renderCell}
              columnCount={
                days.length +
                SUMMARY_COLUMNS.length +
                visibleFixedColumns.length
              }
              columnWidth={({ index }) =>
                index < visibleFixedColumns.length
                  ? visibleFixedColumns[index].width
                  : index - visibleFixedColumns.length < days.length
                    ? DAY_COLUMN_WIDTH
                    : SUMMARY_COLUMNS[
                        index - visibleFixedColumns.length - days.length
                      ].width
              }
              fixedColumnCount={
                width >=
                visibleFixedColumns.reduce(
                  (sum, column) => sum + column.width,
                  0,
                ) +
                  100
                  ? visibleFixedColumns.length
                  : 0
              }
              fixedRowCount={1}
              height={height}
              rowCount={plannerRowsByViewMode.length + 1}
              rowHeight={({ index }) =>
                index === 0 ? HEADER_HEIGHT : ROW_HEIGHT
              }
              width={width}
              scrollToColumn={
                width >=
                visibleFixedColumns.reduce(
                  (sum, column) => sum + column.width,
                  0,
                ) +
                  100
                  ? scrollToTodayColumn
                  : 0
              }
              scrollToAlignment="center"
              style={{ border: "none" }}
              styleTopLeftGrid={{ backgroundColor: headerBackground }}
              styleTopRightGrid={{ backgroundColor: headerBackground }}
              styleBottomLeftGrid={{
                backgroundColor: alpha(theme.palette.background.paper, 0.98),
              }}
              styleBottomRightGrid={{
                backgroundColor: alpha(theme.palette.background.paper, 0.98),
              }}
              overscanColumnCount={2}
              overscanRowCount={5}
            />
          )}
        </AutoSizer>
      </Box>
      <Dialog
        open={Boolean(commentDialogState)}
        onClose={handleCommentDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={600}>
              Planner comment
            </Typography>
            {commentDialogState && (
              <Typography variant="body2" color="text.secondary">
                {commentDialogState.label}
              </Typography>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {commentError && <Alert severity="error">{commentError}</Alert>}
            {commentDialogQuery.error && (
              <Alert severity="warning">
                {(commentDialogQuery.error as Error).message}
              </Alert>
            )}
            {commentDialogQuery.isFetching && !commentDialogQuery.data ? (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              <TextField
                label="Comment"
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                fullWidth
                multiline
                minRows={3}
                helperText="Leave empty to remove the planner comment."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCommentDialogClose}
            disabled={saveCommentMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCommentSave}
            disabled={
              saveCommentMutation.isPending || commentDialogQuery.isFetching
            }
          >
            {saveCommentMutation.isPending ? "Saving..." : "Save comment"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(editorState)}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleFormSubmit}>
          <DialogTitle>
            <Stack spacing={0.5}>
              <Typography variant="h6" fontWeight={600}>
                Planner day details
              </Typography>
              {editorState && (
                <Typography variant="body2" color="text.secondary">
                  {editorState.row.driverName} •{" "}
                  {editorState.date.format("MMMM D, YYYY")}
                </Typography>
              )}
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              {isDifferentPairReadonly && (
                <Alert severity="warning" icon={<WarningAmberRoundedIcon />}>
                  This record exists for a different truck/driver/trailer
                  pairing and is shown in read-only mode.
                </Alert>
              )}
              {isViewModeReadonly && (
                <Alert severity="info">
                  Entries are read-only outside Default view mode. Switch back
                  to Default view to edit.
                </Alert>
              )}
              {formError && <Alert severity="error">{formError}</Alert>}
              <Typography variant="subtitle2">Broker</Typography>
              <Box>
                <Grid container spacing={2}>
                  {(
                    [
                      ["brokerName", "Broker name", 300],
                      ["brokerEmail", "Broker email", 320],
                      ["brokerMcNumber", "Broker MC number", 32],
                      ["brokerDotNumber", "Broker DOT number", 32],
                    ] as const
                  ).map(([field, label, limit]) => (
                    <Grid item xs={12} sm={6} key={field}>
                      <TextField
                        fullWidth
                        label={label}
                        value={formValues[field]}
                        inputProps={{ maxLength: limit }}
                        onChange={handleTextFieldChange(field)}
                        disabled={isEditorReadonly}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
              <BrokerBlacklistWarning
                input={{
                  name: formValues.brokerName,
                  email: formValues.brokerEmail,
                  mcNumber: formValues.brokerMcNumber,
                  dotNumber: formValues.brokerDotNumber,
                }}
                onCheckingChange={setBrokerCheckPending}
              />
              {editorState && (
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dispatcher: {editorState.row.dispatcherName || "Unassigned"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Phone: {editorState.row.phoneNumber || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Company: {editorState.row.companyName || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Truck: {formValues.truckUnitNumber || "N/A"} • Trailer:{" "}
                    {formValues.trailerUnitNumber || "N/A"}
                  </Typography>
                </Stack>
              )}
              <TextField
                select
                fullWidth
                label="Apply to dates"
                value={selectedDates}
                helperText="Pick one or more days in this month to apply the same status and data."
                disabled={isEditorReadonly}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    [...(selected as string[])].sort().join(", "),
                  onChange: handleSelectedDatesChange,
                }}
              >
                {days.map((day) => {
                  const date = currentMonth.date(day);
                  const value = formatPlannerDate(date);
                  return (
                    <MenuItem key={value} value={value} dense>
                      <Checkbox checked={selectedDates.includes(value)} />
                      <ListItemText primary={date.format("MMM D (ddd)")} />
                    </MenuItem>
                  );
                })}
              </TextField>
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Truck #"
                      fullWidth
                      value={formValues.truckUnitNumber}
                      onChange={handleTextFieldChange("truckUnitNumber")}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Trailer #"
                      fullWidth
                      value={formValues.trailerUnitNumber}
                      onChange={handleTextFieldChange("trailerUnitNumber")}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Gross ($)"
                      type="number"
                      inputProps={{ step: 0.01 }}
                      fullWidth
                      value={formValues.gross}
                      onChange={handleTextFieldChange("gross")}
                      disabled={isEditorReadonly}
                      required={formValues.loadPickedUp}
                      error={
                        formValues.loadPickedUp && !formValues.gross.trim()
                      }
                      helperText={
                        formValues.loadPickedUp
                          ? "Required when Load Picked Up is checked."
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mileage"
                      type="number"
                      inputProps={{ step: 0.01 }}
                      fullWidth
                      value={formValues.mileage}
                      onChange={handleTextFieldChange("mileage")}
                      disabled={isEditorReadonly}
                      required={formValues.loadPickedUp}
                      error={
                        formValues.loadPickedUp && !formValues.mileage.trim()
                      }
                      helperText={
                        formValues.loadPickedUp
                          ? "Required when Load Picked Up is checked."
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Destination/Delivery location"
                      fullWidth
                      value={formValues.pickupLocation}
                      onChange={handleTextFieldChange("pickupLocation")}
                      disabled={isEditorReadonly}
                      required={formValues.loadPickedUp}
                      error={pickupLocationError}
                      helperText={
                        pickupLocationError
                          ? "Destination/Delivery location is required when Load Picked Up is selected."
                          : formValues.loadPickedUp
                            ? "Required when Load Picked Up is checked."
                            : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Note"
                      fullWidth
                      multiline
                      minRows={2}
                      value={formValues.emptyOrStuckNote}
                      onChange={handleTextFieldChange("emptyOrStuckNote")}
                      disabled={isEditorReadonly}
                      required={formValues.emptyOrStuck}
                      error={emptyOrStuckNoteError}
                      helperText={
                        emptyOrStuckNoteError
                          ? "Please add a note for empty or stuck loads."
                          : formValues.emptyOrStuck
                            ? "Required when Empty/Stuck is checked."
                            : undefined
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
              <Divider />
              <Typography variant="subtitle2" fontWeight={600}>
                Status
              </Typography>
              <Box>
                <Grid container spacing={1}>
                  {STATUS_OPTIONS.map((option) => {
                    const legend = statusLegendTokens[option.key];
                    return (
                      <Grid item xs={12} sm={6} key={option.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(formValues[option.field])}
                              onChange={handleCheckboxChange(option.field)}
                              disabled={isEditorReadonly}
                            />
                          }
                          label={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: 0.75,
                                  bgcolor: legend.background,
                                  border: `1px solid ${legend.borderColor}`,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body2" color="text.primary">
                                {option.label}
                              </Typography>
                            </Stack>
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRouteFuelDialogOpen(true)}
              startIcon={<LocalGasStationOutlinedIcon />}
            >
              Truck routes & fuel plan
            </Button>
            <Button
              onClick={handleDialogClose}
              disabled={saveEntryMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                saveEntryMutation.isPending ||
                isEditorReadonly ||
                brokerCheckPending
              }
            >
              {saveEntryMutation.isPending
                ? "Saving..."
                : formValues.id
                  ? "Update day"
                  : "Save day"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <TruckRouteFuelDialog
        open={routeFuelDialogOpen}
        plannedStartingLocation={formValues.pickupLocation}
        truckUnitNumber={formValues.truckUnitNumber}
        onClose={() => setRouteFuelDialogOpen(false)}
      />
    </Stack>
  );
};

export default OperationsPlanner;
